import {
  getCompanyInfoApi,
  getJoinCompanyApi,
  getPermsApi,
  getUserCompanyInfoApi,
  getUserInfoApi,
  toOpenCcCenterApi,
} from '~/api/system'
import { isDev } from '~/utils'
import { clearUserId, setUserId } from '~/utils/gio'

export const useMyUserStore = defineStore('myUserStore', {
  state: () => ({
    userId: null,
    userInfo: null as any,
    userIsMain: null,
    userNameCn: null,
    userNameEn: null,

    compVips: null as any,
    compInfo: null as any,
    compStatus: null,
    companyNameCn: null,
    companyNameEn: null,

    tradeFlag: false, //  用户交易权限true有；false没有（默认没登陆的时候没有交易权限）
    shopStatus: null,
    settleStatus: null, // 入驻状态 0 初始， 1 入驻中， 2 已驳回， 3 已入驻
    auditFlag: null, // 商铺审核状态 0 正常， 1 新建待审核， 2 信息异常 人工复审驳回
    isShopCustomerService: false, // 是否为商铺指定客服
    compId: null, // 当前登陆用户公司ID

    joinCompanyInfo: null, // 加入公司待审核信息

    shopImproveStatus: null, // 商铺完善信息状态 ；1已完善，0未完善
    aiRightStatus: null, // Ai权益状态;0：无，1：有
  }),
  getters: {
    currentUserId: (state) => {
      return state.userId
    },
    isChinese: (state) => {
      const countryIdList = [1, 71, 75, 100]
      return countryIdList.includes(state.compInfo?.countryId || 1)
    },
    currentCountryId: (state) => {
      return state?.compInfo?.countryId || 1
    },
    isLogin: (state) => {
      return state.userId !== null
    },
  },
  actions: {
    async getUserCompanyFullInfo() {
      try {
        const res = await getUserCompanyInfoApi()
        const { data } = res as any

        this.userId = data.account.userId
        this.userInfo = data.account
        this.userIsMain = data.account.isMain
        this.userNameCn = data.account.nameCn
        this.userNameEn = data.account.nameEn

        if (data && data.company) {
          this.compVips = data.company.compVips
          this.compInfo = data.company ?? {}
          this.compStatus = data.company.status
          this.companyNameCn = data.company.nameCn
          this.companyNameEn = data.company.nameEn
        }
        // gio 埋点上报用户信息
        setUserId(data.account.userId)
        await this.getPermsInfo(data.perms)
        return data
      } catch (error) {
        console.error('getUserCompanyInfoApi 接口报错', error)
        throw error
      }
    },

    /**
     * 重置用户状态
     *
     * 此方法用于将用户的ID和信息重置为null，以便注销用户或清理用户状态
     */
    resetUser() {
      this.userId = null
      this.userInfo = null
    },

    /**
     * 重置用户和公司信息字段
     * */
    resetUserCompInfo() {
      this.userId = null
      this.userInfo = null
      this.userNameCn = null
      this.userNameEn = null
      this.userIsMain = null

      this.compVips = null
      this.compInfo = null
      this.companyNameCn = null
      this.companyNameEn = null
      this.compStatus = null

      this.tradeFlag = false
      this.shopStatus = null
      this.settleStatus = null
      this.auditFlag = null
      this.shopImproveStatus = null
      this.aiRightStatus = null
      this.compId = null

      // gio 清除用户 ID
      clearUserId()
    },

    getDevData() {
      const data = devData
      return data
    },

    /**
     * 获取用户和公司信息
     * 此函数根据用户ID获取最新的用户和公司信息
     * 它会检查是否需要强制刷新公司信息
     *
     * @param compInfoRefreshFlag - 是否强制刷新公司信息，默认为false
     */
    async getUserCompanyInfo() {
      try {
        let data

        if (isDev()) {
          data = this.getDevData()
        } else {
          const res: any = await getUserCompanyInfoApi()
          console.log('🍓🍓 ~ user.ts:140 ~ getUserCompanyInfo ~ res:', res)
          // 业务 401 时 jcFetch 可能仍 resolve body，勿当成功写入 store
          if (res?.code === 401 || !res?.data?.account) {
            throw Object.assign(
              new Error(res?.msg || res?.describe || 'auth failed'),
              {
                code: res?.code ?? 401,
                data: res,
              },
            )
          }
          data = res.data
        }
        console.log('🍓🍓 ~ user.ts:141 ~ getUserCompanyInfo ~ data:', data)

        this.userId = data.account.userId
        this.userInfo = data.account
        this.userIsMain = data.account.isMain
        this.userNameCn = data.account.nameCn
        this.userNameEn = data.account.nameEn

        if (data.company) {
          this.compVips = data.company.compVips
          this.compInfo = data.company ?? {}
          this.compStatus = data.company.status
          this.companyNameCn = data.company.nameCn
          this.companyNameEn = data.company.nameEn
        }
        setUserId(data.account.userId)
        await this.getPermsInfo(data.perms)
        return data
      } catch (err) {
        console.error('获取用户和公司信息时出错:', err)
        throw err
      }
    },

    /**
     * 异步获取权限信息
     *
     * 此方法旨在根据当前环境（开发或生产）来获取相应的权限信息
     * 在开发环境中直接返回一组固定的数据，用于模拟权限信息
     * 在生产环境中，则通过调用后端接口来获取实际的权限信息
     */
    async getPermsInfo(permsData: any) {
      if (!permsData || !permsData[getAppId()]) return

      let data = null
      // 在开发环境中，直接返回模拟的权限信息
      // 确保 isDev() 在上下文中可用；否则换成合适的判断
      if (isDev && typeof isDev === 'function' && isDev()) {
        data = {
          compId: 5006000,
          shopStatus: '0',
          settleStatus: '3',
          auditFlag: '0',
          trxFlag: '1',
          shopImproveStatus: '0',
          aiRightStatus: '0',
        }
      } else {
        // 在生产环境中，通过调用接口获取权限信息
        data = JSON.parse(permsData[getAppId()] || '{}')
      }

      this.tradeFlag = data.trxFlag === '1'
      this.shopStatus = data.shopStatus
      this.auditFlag = data.auditFlag
      this.settleStatus = data.settleStatus
      this.shopImproveStatus = data.shopImproveStatus
      this.compId = data.compId
      this.aiRightStatus = data.aiRightStatus
    },

    // 获取是否加入公司待审核状态
    getJoinCompanyAudit() {
      return getJoinCompanyApi().then((res: any) => {
        this.joinCompanyInfo = res.data
        return res
      })
    },
  },

  persist: {
    storage: persistedState.localStorage,
  },
})
